import React, { lazy, Suspense } from 'react';
import { connect } from 'react-redux';
import { scroller } from 'react-scroll';
import { Translation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { PlaneTakeoff } from 'lucide-react';

import { Redirect, Link } from 'react-router-dom';

import TermsPrivacyFee from '../../../components/Layout/TermsPrivacyFee/TermsPrivacyFee';

import PropagateLoader from '../../../components/Layout/PropagateLoader';

// components
// import Alert from '../../../components/Layout/Alert/Alert';

//actions
import { loginAction } from '../../../actions/auth';
import { setToLoading, setToLoaded } from '../../../actions/loader';

import { setLang } from '../../../actions/lang';
import { getLangCode } from '../../../services/lang';
import { setAlert } from '../../../services/sweetAlert';
// assets
import config from '../../../config';
import { COLD_START_RETRY_INTERVAL_MS, COLD_START_MAX_RETRIES } from '../../../coldStartConfig';

const ReCAPTCHA = lazy(() => import('react-google-recaptcha'));

class Login extends React.Component {
  constructor(props) {
    super(props);

    this._retryTimer = null;
    this._healthTimer = null;

    this.state = {
      email: '',
      password: '',
      recaptcha: '',
      isLoading: false,
      isColdStarting: false,
      coldStartAttempt: 0,
      serverReady: true
    };
  }

  componentDidMount() {
    this._scrollToTop();
    this.captcha && this.captcha.reset();
    console.log(this.props);
    this._checkServerHealth();
  }
  componentWillMount() {
    this.captcha && this.captcha.reset();
    // this.props.setLang(this.props.isEn ? "en": "cn")
  }

  _scrollToTop() {
    scroller.scrollTo('login-container', {
      duration: 800,
      smooth: true,
      offset: -200
    });
  }

  onChange(e) {
    this.setState({
      [e.target.name]: e.target.value
    });
  }

  handleReCaptcha(val) {
    this.setState({
      recaptcha: val
    });
  }

  _validateInput() {
    // let isInputValid = true;
    let errors = [];

    if (this.state.email.length == 0) {
      errors.push('Please enter a valid email.');
    }

    if (this.state.password.length == 0) {
      errors.push('Password cannot be empty.');
    }

    if (errors.length > 0) {
      setAlert({ icon: 'danger', message: errors[0] });
      return false;
    }
    return true;
  }

  componentWillUnmount() {
    if (this._retryTimer) {
      clearTimeout(this._retryTimer);
      this._retryTimer = null;
    }
    if (this._healthTimer) {
      clearTimeout(this._healthTimer);
      this._healthTimer = null;
    }
  }

  async _checkServerHealth() {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 4000);
      const res = await fetch(`${config.service}/health`, { signal: controller.signal });
      clearTimeout(timeoutId);
      if (res.ok) return;
    } catch (e) {
      // server is down or unreachable
    }
    this.setState({ serverReady: false });
    this._healthTimer = setTimeout(() => this._pollServerHealth(), COLD_START_RETRY_INTERVAL_MS);
  }

  async _pollServerHealth() {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 4000);
      const res = await fetch(`${config.service}/health`, { signal: controller.signal });
      clearTimeout(timeoutId);
      if (res.ok) {
        this.setState({ serverReady: true });
        return;
      }
    } catch (e) {
      // still down
    }
    this._healthTimer = setTimeout(() => this._pollServerHealth(), COLD_START_RETRY_INTERVAL_MS);
  }

  async _attemptLogin() {
    try {
      let username = this.state.email.split(' ').join('').toLowerCase();
      let res = await this.props.loginAction(username, this.state.password, this.state.recaptcha);
      this.props.setToLoaded();

      if (!res.success) {
        if (this.captcha) this.captcha.reset();
        this.setState({ recaptcha: '' });
        setAlert({ icon: 'danger', message: res.extras.message || 'Login Failed' });
      }
    } catch (e) {
      this.props.setToLoaded();
      if (this.captcha) this.captcha.reset();
      this.setState({ recaptcha: '', serverReady: false });
      this._checkServerHealth();
    }
  }

  async emailSubmit(e) {
    e.preventDefault();

    if (!this._validateInput()) return;

    this.props.setToLoading();
    this.setState({ isColdStarting: false, coldStartAttempt: 0 });

    await this._attemptLogin();
  }

  render() {
    if (this.props.isAuthenticated) {
      return <Redirect to="/dashboard"></Redirect>;
    }

    let buttonDisabled = !(this.state.email && this.state.password && this.state.recaptcha) || this.state.isLoading;

    return (
      <>
        <Helmet>
          <title>South Vancouver Medical Clinic | Login</title>
          <link rel="canonical" href="https://southvanmed.com/login" />

          <meta name="description" content="The South Vancouver Medical Clinic login page " />
        </Helmet>

        <section className="login-area pt-100 pb-100">
          <div id="login-container" className="container">
            <div className="row">
              <div className="col-lg-6 offset-lg-3">
                <div className="basic-login">
                  <h3 className="text-center mb-30">Login</h3>
                  <p className="text-center mb-30">Login for appointments, forms, sick notes, medication refills and more services!</p>

                  {this.state.serverReady === false ? (
                    <div data-testid="cold-start-banner" style={{ textAlign: 'center', padding: '30px 10px' }}>
                      <i className="fas fa-spinner fa-spin" style={{ color: '#0275d8', fontSize: '32px' }}></i>
                      <p style={{ color: '#004085', fontSize: '15px', marginTop: '16px', marginBottom: '8px' }}>
                        Our server is waking up during quiet hours.
                      </p>
                      <p style={{ color: '#6c8ebf', fontSize: '13px', margin: 0 }}>
                        This usually takes about a minute. The login form will appear automatically — no need to refresh.
                      </p>
                    </div>
                  ) : (
                  <>
                  <form onSubmit={this.emailSubmit.bind(this)}>
                    <Translation>
                      {t => (
                        <>
                          <label htmlFor="email" className="mb-2">
                            Email Address <span>**</span>
                          </label>

                          <input
                            className="fill-content"
                            type="text"
                            name="email"
                            onChange={this.onChange.bind(this)}
                            value={this.state.email}
                            placeholder={t('Email address ...')}
                            autoComplete="username"
                          />
                        </>
                      )}
                    </Translation>
                    <br />
                    <Translation>
                      {t => (
                        <>
                          <label htmlFor="password" className="mb-2">
                            Password <span>**</span>
                          </label>
                          <input
                            className="fill-content"
                            type="password"
                            name="password"
                            onChange={this.onChange.bind(this)}
                            value={this.state.password}
                            placeholder={t('Enter password')}
                            autoComplete="current-password"
                          />
                        </>
                      )}
                    </Translation>
                    <br />
                    <div>
                      <Suspense fallback={<PropagateLoader isLoading={true} />}>
                        <ReCAPTCHA ref={e => (this.captcha = e)} className="g-recaptcha" sitekey={config.captcha_key} onChange={this.handleReCaptcha.bind(this)} hl={getLangCode()} />
                      </Suspense>
                    </div>
                    <br />

                    <div className="link-container">
                      <div className="signup-section">
                        <span className="minor-text">
                          <Translation>{t => <>{t('New to South Vancouver Medical Clinic?')}</>}</Translation>
                        </span>{' '}
                        <br></br>
                        <Link className="link-signup" to="/register">
                          <Translation>{t => <>{t('Sign Up')}</>}</Translation>
                        </Link>
                      </div>
                    </div>
                    <br />
                    <button className="primary_btn" disabled={buttonDisabled} type="submit">
                      <Translation>{t => <>{t('Log in')}</>}</Translation>
                    </button>
                    <div className="forgot-section text-center">
                      <Translation>
                        {t => (
                          <Link className="link" to="/forgotpassword">
                            {t('Forgot password')}
                          </Link>
                        )}
                      </Translation>
                    </div>
                  </form>
                  <div className="or-divide">
                    <span>or</span>
                  </div>

                  <Link className="primary_btn w-100 btn-icon btn-hide" to="/immi">
                    {' '}
                    <span>
                      <PlaneTakeoff size={25} />
                    </span>{' '}
                    <Translation>
                      {t => (
                        <>
                          {t('Book Immigration ')}
                          <div className="hide-on-mobile">Medical Exam</div>
                        </>
                      )}
                    </Translation>
                  </Link>

                  <br />
                  <br />
                  <TermsPrivacyFee />
                  </>
                  )}
                </div>
                <div className="floating-btn-container"></div>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }
}

const mapStateToProps = state => {
  return {
    isAuthenticated: state.auth.isAuthenticated,
    alertMessage: state.auth.alertMessage,
    isEn: state.lang.lang == 'en' || typeof state.lang.lang == 'undefined'
  };
};

const mapDispatchToProps = {
  loginAction,
  setLang,
  setToLoading,
  setToLoaded
};
export default connect(mapStateToProps, mapDispatchToProps)(Login);
